// src/pages/LeadSources.jsx
// Settings page: webhook URLs, tokens, per-source stats
// Admin only

import React, { useState, useEffect, useCallback } from 'react';
import { LeadIntake } from '../services/api';
import { useAuth, ROLES } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const SOURCE_ICONS = {
    meta:'📘', google:'🔍', indiamart:'🏭',
    tradeindia:'🤝', justdial:'📞', manual:'📋', custom:'🔗',
};

const SETUP_GUIDES = {
    meta: {
        title: 'Meta Lead Ads Setup',
        steps: [
            'Go to Meta Business Suite → All Tools → Leads Centre',
            'Click "CRM Integration" → "Other CRMs"',
            'Select "Webhooks" and paste the webhook URL below',
            'For Verify Token: paste your webhook token',
            'Subscribe to leads_retrieval event',
            'Meta will send a GET verification request first — handled automatically',
        ],
    },
    google: {
        title: 'Google Ads Lead Form Setup',
        steps: [
            'In Google Ads: Campaigns → Assets → Lead Form',
            'Under "Lead delivery" → choose "Webhook"',
            'Paste the webhook URL below',
            'For Google Forms: use Apps Script to POST to this URL on form submit',
        ],
    },
    indiamart: {
        title: 'IndiaMart Setup',
        steps: [
            'Login to seller.indiamart.com',
            'Go to Lead Manager → Settings → Push Notifications',
            'Enter the webhook URL below',
            'Select lead types to receive',
            'IndiaMart also supports API polling — contact support for GLID key',
        ],
    },
    tradeindia: {
        title: 'TradeIndia Setup',
        steps: [
            'Login to seller.tradeindia.com',
            'Go to Lead Manager → Webhook Settings',
            'Paste the webhook URL below',
            'Select "JSON" as the format',
        ],
    },
    justdial: {
        title: 'JustDial Setup',
        steps: [
            'Contact your JustDial account manager',
            'Request "API Push" integration for leads',
            'Provide the webhook URL below',
            'JustDial will configure on their end',
        ],
    },
    custom: {
        title: 'Custom Webhook',
        steps: [
            'Any platform that supports webhooks can POST to this URL',
            'Send JSON with fields: name, phone, email, company, city, product, message',
            'Include ?token=YOUR_TOKEN in the URL for authentication',
        ],
    },
};

export default function LeadSources() {
    const { user }    = useAuth();
    const navigate    = useNavigate();
    const [sources,   setSources]   = useState([]);
    const [busy,      setBusy]      = useState(true);
    const [expanded,  setExpanded]  = useState(null);
    const [copied,    setCopied]    = useState(null);
    const [regen,     setRegen]     = useState(null);
    const [err,       setErr]       = useState('');

    useEffect(() => {
        if (user.ryts !== '1' && user.ryts !== 1) navigate('/leads');
    }, [user]);

    const load = useCallback(() => {
        setBusy(true);
        LeadIntake.sources()
            .then(setSources)
            .catch(e => setErr(e.message))
            .finally(() => setBusy(false));
    }, []);
    useEffect(load, [load]);

    const copyURL = (url, id) => {
        navigator.clipboard.writeText(url);
        setCopied(id);
        setTimeout(() => setCopied(null), 2000);
    };

    const handleRegen = async (source) => {
        if (!window.confirm(`Regenerate token for ${source.name}? The old webhook URL will stop working immediately.`)) return;
        setRegen(source.id);
        try {
            const res = await LeadIntake.regenerateToken(source.id);
            if (res.success) { load(); }
        } catch (ex) { setErr(ex.message); }
        finally { setRegen(null); }
    };

    const StatPill = ({ label, count, color, bg }) => (
        <div style={{ background:bg, color, border:`1px solid ${color}30`,
            borderRadius:8, padding:'4px 12px', textAlign:'center', minWidth:60 }}>
            <div style={{ fontSize:'1.1rem', fontWeight:800 }}>{count}</div>
            <div style={{ fontSize:'.65rem', fontWeight:600, textTransform:'uppercase', letterSpacing:'.05em' }}>{label}</div>
        </div>
    );

    if (busy) return <div className="spinner-wrap"><div className="spinner" /></div>;

    return (
        <div className="page">
            <div className="page-header">
                <div className="page-title">🔗 Lead Sources & Webhooks</div>
            </div>

            {err && (
                <div className="alert alert-error" style={{ marginBottom:'1rem' }}>
                    {err}
                    <button onClick={() => setErr('')}
                        style={{ marginLeft:8, background:'none', border:'none', cursor:'pointer' }}>✕</button>
                </div>
            )}

            <div style={{ fontSize:'.85rem', color:'var(--muted)', marginBottom:'1.5rem', maxWidth:600 }}>
                Each platform gets a unique webhook URL. Paste it into the platform's settings.
                Leads arrive automatically and appear in the Inbound Leads inbox.
            </div>

            <div style={{ display:'flex', flexDirection:'column', gap:'1rem' }}>
                {sources.map(s => {
                    const isOpen  = expanded === s.id;
                    const guide   = SETUP_GUIDES[s.slug];
                    const hasURL  = !!s.webhook_url;

                    return (
                        <div key={s.id} style={{
                            background:'var(--card-bg,#121b32)',
                            border:'1px solid var(--border,#e2e8f0)',
                            borderRadius:14,
                            overflow:'hidden',
                        }}>
                            {/* ── Source header row ── */}
                            <div
                                style={{
                                    padding:'1rem 1.25rem',
                                    display:'flex', alignItems:'center',
                                    gap:'1rem', cursor:'pointer',
                                    background: isOpen ? 'var(--bg,#f8fafc)' : 'transparent',
                                    borderBottom: isOpen ? '1px solid var(--border,#e2e8f0)' : 'none',
                                }}
                                onClick={() => setExpanded(isOpen ? null : s.id)}
                            >
                                <span style={{ fontSize:'1.6rem' }}>{SOURCE_ICONS[s.slug] || '📌'}</span>

                                <div style={{ flex:1 }}>
                                    <div style={{ fontWeight:700, fontSize:'1rem' }}>{s.name}</div>
                                    <div style={{ fontSize:'.75rem', color:'var(--muted)' }}>
                                        {hasURL ? '● Webhook active' : '○ Manual import only'}
                                        {s.last_lead_at && ` · Last lead: ${new Date(s.last_lead_at).toLocaleDateString('en-IN')}`}
                                    </div>
                                </div>

                                {/* Stats */}
                                <div style={{ display:'flex', gap:'.4rem' }}>
                                    <StatPill label="Total"     count={s.total_leads} color="#374151" bg="#f3f4f6" />
                                    <StatPill label="New"       count={s.new_leads}   color="#1e40af" bg="#dbeafe" />
                                    <StatPill label="Converted" count={s.converted}   color="#166534" bg="#dcfce7" />
                                </div>

                                <span style={{ color:'var(--muted)', fontSize:'1.1rem' }}>
                                    {isOpen ? '▲' : '▼'}
                                </span>
                            </div>

                            {/* ── Expanded detail ── */}
                            {isOpen && (
                                <div style={{ padding:'1.2rem 1.25rem' }}>
                                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1.5rem' }}>

                                        {/* Webhook URL */}
                                        {hasURL && (
                                            <div>
                                                <div style={{ fontSize:'.72rem', fontWeight:700,
                                                    textTransform:'uppercase', letterSpacing:'.05em',
                                                    color:'var(--muted)', marginBottom:'.5rem' }}>
                                                    Webhook URL
                                                </div>
                                                <div style={{ display:'flex', gap:'.4rem' }}>
                                                    <input
                                                        readOnly
                                                        value={s.webhook_url}
                                                        style={{
                                                            flex:1, fontSize:'.72rem', fontFamily:'monospace',
                                                            background:'#0f172a', color:'#94a3b8',
                                                            border:'none', borderRadius:8,
                                                            padding:'7px 10px', overflowX:'auto',
                                                        }}
                                                        onClick={e => e.target.select()}
                                                    />
                                                    <button
                                                        className="btn btn-sm btn-ghost"
                                                        onClick={() => copyURL(s.webhook_url, s.id)}
                                                        style={{ flexShrink:0, whiteSpace:'nowrap' }}>
                                                        {copied === s.id ? '✓ Copied' : '📋 Copy'}
                                                    </button>
                                                </div>

                                                {/* Regen token */}
                                                <button
                                                    onClick={() => handleRegen(s)}
                                                    disabled={regen === s.id}
                                                    style={{
                                                        marginTop:'.5rem', background:'none', border:'none',
                                                        cursor:'pointer', fontSize:'.72rem', color:'#dc2626',
                                                        textDecoration:'underline', padding:0,
                                                    }}>
                                                    {regen === s.id ? 'Regenerating…' : '⚠ Regenerate token'}
                                                </button>
                                                <div style={{ fontSize:'.68rem', color:'var(--muted)', marginTop:2 }}>
                                                    Regenerating will break the existing integration until you update the URL.
                                                </div>
                                            </div>
                                        )}

                                        {/* Setup guide */}
                                        {guide && (
                                            <div>
                                                <div style={{ fontSize:'.72rem', fontWeight:700,
                                                    textTransform:'uppercase', letterSpacing:'.05em',
                                                    color:'var(--muted)', marginBottom:'.5rem' }}>
                                                    {guide.title}
                                                </div>
                                                <ol style={{ paddingLeft:'1.1rem', margin:0 }}>
                                                    {guide.steps.map((step, i) => (
                                                        <li key={i} style={{ fontSize:'.78rem', color:'var(--text)',
                                                            marginBottom:'.3rem', lineHeight:1.5 }}>
                                                            {step}
                                                        </li>
                                                    ))}
                                                </ol>
                                            </div>
                                        )}
                                    </div>

                                    {/* Per-source stats */}
                                    <div style={{
                                        marginTop:'1rem', display:'flex', gap:'.5rem', flexWrap:'wrap',
                                        padding:'10px 14px', background:'var(--bg,#f8fafc)', borderRadius:10,
                                    }}>
                                        {[
                                            { label:'Total',      count:s.total_leads, color:'#374151', bg:'#f3f4f6' },
                                            { label:'New',        count:s.new_leads,   color:'#1e40af', bg:'#dbeafe' },
                                            { label:'Converted',  count:s.converted,   color:'#166534', bg:'#dcfce7' },
                                            { label:'Duplicates', count:s.duplicates,  color:'#92400e', bg:'#fef3c7' },
                                            { label:'Junk',       count:s.junk,        color:'#6b7280', bg:'#f3f4f6' },
                                        ].map(p => <StatPill key={p.label} {...p} />)}
                                        {s.total_leads > 0 && (
                                            <div style={{ display:'flex', alignItems:'center',
                                                marginLeft:'auto', fontSize:'.82rem', color:'var(--muted)' }}>
                                                Conversion rate:&nbsp;
                                                <strong style={{ color:'#166534' }}>
                                                    {s.total_leads > 0
                                                        ? Math.round(s.converted / s.total_leads * 100) + '%'
                                                        : '—'}
                                                </strong>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
